import { FlowType } from '@jerry-serverless/types';

import type { AppData } from '../../types/app_data';
import type { Step } from '../types/step';

import { PartnerStep } from './partner.step';
import { MALStep } from './mal.step';
import { ServiceSplitStep } from './servicesplit.step';
import { IntroDriveCountStep } from './introdrivecount.step';
import { IntroGoodDriverStep } from './introgooddriver.step';
import { AddCarYearBeforeOnboardingStep } from './addcaryear.beforeonboarding.step';
import { AddCarStepsBeforeOnboardingSteps } from './addcars.step';
import { CarsStepBeforeOnboardingStep } from './cars.step';
import { RefiCarsBeforeOnboardingStep } from './reficars.step';
import { AddMaintenanceReminderBeforeOnboardingSteps } from './addmaintenance.beforeonboarding.step';
import { MinNumberPaymentsBeforeOnboardingStep } from './minnumberpayments.step';
import { EstimatedMileageBeforeOnboardingStep } from './estimatedmileage';
import { ShopForStep } from './shopfor.step';
import { InsuredBeforeOnboardingStep } from './insured.step';
import { CreditRangeBeforeOnboardingStep } from './creditrange.step';
import { NotInsuredBeforeOnboardingStep } from './notinsured.step';
import { InsuredHistoryBeforeOnboardingStep } from './insuredhistory.step';
import { ClaimsBeforeOnboardingStep } from './claims.step';
import { ViolationsCountBeforeOnboardingStep } from './violationcount.step';
import { IncomeBeforeOnboardingStep } from './income.step';
import { HomeOwnerBeforeOnboardingStep } from './homeownership.step';
import { NameStep } from './name.step';
import { MiddleNameStep } from './middlename.step';
import { ConfirmMiddleNameStep } from './confirmmiddlename.step';
import { AcceptInviteStep } from './acceptinvite';
import { InviteLoginStep } from './inviteelogin.step';
import { PrefillPhoneStep } from './prefillphone.step';
import { PhoneStep } from './phone.step';
import { VerifyStep } from './verify.step';
import { PrefillConfirmInfoStep } from './prefillconfirminfo.step';
// GarageOnlyFlow steps
import { GarageOnlyStartStep } from './garageonly/garageonlystart.step';
import { GarageOnlyServiceAllSetStep } from './garageonly/garageonlyserviceallset.step';
import { NameStep as GarageOnlyNameStep } from './garageonly/name.step';
import { MiddleNameStep as GarageOnlyMiddleNameStep } from './garageonly/middlename.step';
import { ConfirmMiddleNameStep as GarageOnlyConfirmMiddleNameStep } from './garageonly/confirmmiddlename.step';
import { PhoneStep as GarageOnlyPhoneStep } from './garageonly/phone.step';
import { VerifyStep as GarageOnlyVerifyStep } from './garageonly/verify.step';
import {
  GarageOnlyAddCarStepsBeforeOnboardingSteps,
  GarageOnlyAddCarMileageReminderStep,
  GarageOnlyAddCarMileageValueStep,
} from './garageonly/addcars.step';
import { GarageOnlyAddCarMileage } from './garageonly/addcarmileage.step';
import { GarageOnlyReminder } from './garageonly/garageonlyreminder.step';
import { GarageOnlyAddMaintenanceReminderSteps } from './garageonly/garageonlyaddmaintenance.step';
import { GarageOnlyCars } from './garageonly/garageonlycars.step';
import { GarageOnlyCarValueMonthlyUpdate } from './garageonly/garageonlycarvaluemonthlyupdate.step';
import { GarageOnlyFutureRecall } from './garageonly/garageonlyfuturerecall.step';
import { GarageOnlyAccount } from './garageonly/garageonlyaccount.step';
import { GarageOnlyEstimateServiceStep } from './garageonly/garageonlyestimateservice.step';
import { GarageOnlyServiceDetailStep } from './garageonly/garageonlyservicedetail.step';
import { GarageOnlyMileageSetStep } from './garageonly/garageonlymileageset.step';
import { GarageOnlyMaintenanceReminderStep } from './garageonly/garageonlymaintenance.step';
import { GarageOnlyCarValueAllSetStep } from './garageonly/garageonlycarvalueallset.step';
import { CoverageStep } from './coverage.step';
import { GarageOnlyRecallFoundStep } from './garageonly/garageonlyrecallfound.step';
import { GarageOnlyRecallQueryStep } from './garageonly/garageonlyrecallquery.step';
import { GarageOnlyRecallAlertsStep } from './garageonly/garageonlyrecallalerts.step';
import { GarageOnlyRecallAlertsSetStep } from './garageonly/garageonlyrecallalertsset.step';
import { NameAndPhoneStep as GarageOnlyNameAndPhoneStep } from './garageonly/nameandphone.step';

const defaultSteps: Step[] = [
  PartnerStep,
  PrefillConfirmInfoStep,
  PrefillPhoneStep,
  MALStep,
  ServiceSplitStep,
  IntroDriveCountStep,
  IntroGoodDriverStep,
  AddCarYearBeforeOnboardingStep,
  /**
   * PM decided to temporarily open the NNO entrance only to users who choose the insurance service,
   * so steps for other flows should be commented out first.
   */
  // ...NNOStepsBeforeOnboarding,
  ...AddCarStepsBeforeOnboardingSteps,
  CarsStepBeforeOnboardingStep,
  RefiCarsBeforeOnboardingStep,
  ...AddMaintenanceReminderBeforeOnboardingSteps,
  MinNumberPaymentsBeforeOnboardingStep,
  EstimatedMileageBeforeOnboardingStep,
  ShopForStep,
  InsuredBeforeOnboardingStep,
  NotInsuredBeforeOnboardingStep,
  InsuredHistoryBeforeOnboardingStep,
  CoverageStep,

  ClaimsBeforeOnboardingStep,
  ViolationsCountBeforeOnboardingStep,
  IncomeBeforeOnboardingStep,
  HomeOwnerBeforeOnboardingStep,
  CreditRangeBeforeOnboardingStep,

  NameStep,
  MiddleNameStep,
  ConfirmMiddleNameStep,
  InviteLoginStep,
  AcceptInviteStep,
  PhoneStep,
  VerifyStep,
];

const repairEstimateFlowSteps: Step[] = [
  GarageOnlyStartStep,
  ...GarageOnlyAddCarStepsBeforeOnboardingSteps,
  // Removed as requested by PM
  // GarageOnlyCarAllSetStep,
  GarageOnlyEstimateServiceStep,
  GarageOnlyServiceDetailStep,
  GarageOnlyServiceAllSetStep,
  GarageOnlyReminder,
  // GarageOnlyReminderSkipStep,
  ...GarageOnlyAddMaintenanceReminderSteps,
  GarageOnlyAddCarMileageReminderStep,
  // GarageOnlyReminderAllSetStep,
  // Removed from https://app.asana.com/0/1207252993552442/1209300190756268
  // GarageOnlyCarValue,
  // GarageOnlyCarValueSkipStep,
  // GarageOnlyAddCarMileageValueStep,
  // GarageOnlyCarValueResult,
  GarageOnlyCarValueMonthlyUpdate,
  // GarageOnlyCarValueMonthlyUpdateSkipStep,
  // GarageOnlyCarValueMonthlyUpdateAllSet,
  // GarageOnlyRecallResult,
  GarageOnlyFutureRecall,
  GarageOnlyAccount,
  GarageOnlyNameStep,
  GarageOnlyMiddleNameStep,
  GarageOnlyConfirmMiddleNameStep,
  GarageOnlyPhoneStep,
  GarageOnlyVerifyStep,
];

const repairShopFlowSteps: Step[] = [
  GarageOnlyStartStep,
  ...GarageOnlyAddCarStepsBeforeOnboardingSteps,
  GarageOnlyEstimateServiceStep,
  GarageOnlyServiceDetailStep,
  GarageOnlyAccount,
  GarageOnlyNameStep,
  GarageOnlyMiddleNameStep,
  GarageOnlyConfirmMiddleNameStep,
  GarageOnlyPhoneStep,
  GarageOnlyVerifyStep,
];

const carValueFlowSteps: Step[] = [
  ...GarageOnlyAddCarStepsBeforeOnboardingSteps,
  GarageOnlyAddCarMileageValueStep,
  GarageOnlyMileageSetStep,

  GarageOnlyRecallAlertsStep,
  GarageOnlyRecallAlertsSetStep,

  GarageOnlyCarValueAllSetStep,
  GarageOnlyNameStep,
  GarageOnlyMiddleNameStep,
  GarageOnlyConfirmMiddleNameStep,
  GarageOnlyPhoneStep,

  GarageOnlyNameAndPhoneStep,
  GarageOnlyVerifyStep,
];

const carRecallFlowSteps: Step[] = [
  ...GarageOnlyAddCarStepsBeforeOnboardingSteps,
  GarageOnlyRecallQueryStep,
  GarageOnlyRecallFoundStep,
  GarageOnlyMaintenanceReminderStep,
  ...GarageOnlyAddMaintenanceReminderSteps,

  GarageOnlyCarValueAllSetStep,
  GarageOnlyNameStep,
  GarageOnlyMiddleNameStep,
  GarageOnlyConfirmMiddleNameStep,
  GarageOnlyPhoneStep,
  GarageOnlyVerifyStep,
];

const maintenanceFlowSteps: Step[] = [
  ...GarageOnlyAddCarStepsBeforeOnboardingSteps,
  GarageOnlyAddCarMileage,
  GarageOnlyCars,
  GarageOnlyNameStep,
  GarageOnlyMiddleNameStep,
  GarageOnlyConfirmMiddleNameStep,
  GarageOnlyPhoneStep,
  GarageOnlyVerifyStep,
];

export const getCreateAccountSteps = (flowType?: AppData['flowType']): Step[] => {
  let steps = defaultSteps;
  if (flowType === FlowType.RepairEstimate) {
    steps = repairEstimateFlowSteps;
  }
  if (flowType === FlowType.RepairShop) {
    steps = repairShopFlowSteps;
  }
  if (flowType === FlowType.CarValue) {
    steps = carValueFlowSteps;
  }
  if (flowType === FlowType.CarRecall) {
    steps = carRecallFlowSteps;
  }
  if (flowType === FlowType.Maintenance) {
    steps = maintenanceFlowSteps;
  }
  return steps;
};
